import { Device, Group, License } from "@devices/types";
import SemverService from "@services/semverService/semverService";

export type GroupSelectionState = "none" | "partial" | "all";

type Incompatibility = {
    required: string;
    current: string;
};

export type DeviceTreeNode = {
    type: "device";
    id: number;
    name: string;
    serial: string | null;
    groupId: number;
    incompatibility: Incompatibility | null;
};

export type GroupTreeNode = {
    type: "group";
    id: number;
    name: string;
    parentId: number | null;
    children: GroupTreeNode[];
    devices: DeviceTreeNode[];
    totalDeviceCount: number;
};

export type SearchResult = {
    matchingDeviceIds: Set<number>;
    groupsToExpand: Set<number>;
};

export const getAllGroupTreeNodeIds = (tree: GroupTreeNode[]): number[] => {
    return tree.flatMap((g) => [g.id, ...getAllGroupTreeNodeIds(g.children)]);
};

// A device that is known not to accept the build. Unknown versions or ranges never block.
const getIncompatibility = (device: Device, compatibility: string | null | undefined): Incompatibility | null => {
    const current = device.lastState?.version?.emteriaVersion;
    if (!current || !compatibility || SemverService.satisfies(current, compatibility) !== false) {
        return null;
    }
    return { required: compatibility.trim(), current };
};

const isSelectable = (device: DeviceTreeNode) => device.incompatibility === null;

export const getSelectableDeviceIdsInGroup = (group: GroupTreeNode): number[] => {
    const directIds = group.devices.filter(isSelectable).map((d) => d.id);
    const childIds = group.children.flatMap(getSelectableDeviceIdsInGroup);
    return [...directIds, ...childIds];
};

export const getGroupSelectionState = (group: GroupTreeNode, selectedDeviceIds: Set<number>): GroupSelectionState => {
    const allDeviceIds = getSelectableDeviceIdsInGroup(group);

    if (allDeviceIds.length === 0) {
        return "none";
    }

    const selectedCount = allDeviceIds.filter((id) => selectedDeviceIds.has(id)).length;

    if (selectedCount === 0) {
        return "none";
    }
    if (selectedCount === allDeviceIds.length) {
        return "all";
    }
    return "partial";
};

export const searchDevicesInTree = (tree: GroupTreeNode[], query: string): SearchResult => {
    if (!query.trim()) {
        return { matchingDeviceIds: new Set(), groupsToExpand: new Set() };
    }

    const lowerQuery = query.toLowerCase();
    const matchingDeviceIds = new Set<number>();
    const groupsToExpand = new Set<number>();

    const searchGroup = (group: GroupTreeNode, ancestorIds: number[]): boolean => {
        let hasMatch = false;

        group.devices.forEach((device) => {
            if (device.name?.toLowerCase().includes(lowerQuery) || device.serial?.toLowerCase().includes(lowerQuery)) {
                matchingDeviceIds.add(device.id);
                hasMatch = true;
            }
        });

        group.children.forEach((child) => {
            if (searchGroup(child, [...ancestorIds, group.id])) {
                hasMatch = true;
            }
        });

        if (hasMatch) {
            ancestorIds.forEach((id) => groupsToExpand.add(id));
            groupsToExpand.add(group.id);
        }

        return hasMatch;
    };

    tree.forEach((group) => searchGroup(group, []));

    return { matchingDeviceIds, groupsToExpand };
};

/**
 * When searching, filters tree to only show groups that contain matching devices (or are ancestors of such groups).
 */
export const filterTreeBySearch = (tree: GroupTreeNode[], groupsToExpand: Set<number>): GroupTreeNode[] => {
    if (groupsToExpand.size === 0) {
        return [];
    }
    return tree
        .filter((group) => groupsToExpand.has(group.id))
        .map((group) => ({
            ...group,
            children: filterTreeBySearch(group.children, groupsToExpand),
        }));
};

export const getSelectableDeviceIdsInTree = (tree: GroupTreeNode[]): number[] => {
    return tree.flatMap(getSelectableDeviceIdsInGroup);
};

export type FlatTreeItem =
    | { type: "group"; group: GroupTreeNode; depth: number }
    | { type: "device"; device: DeviceTreeNode; groupId: number; depth: number };

export const flattenVisibleTree = (
    tree: GroupTreeNode[],
    expandedGroups: Set<number>,
    searchResult: SearchResult,
    depth = 0,
): FlatTreeItem[] => {
    const result: FlatTreeItem[] = [];

    for (const group of tree) {
        result.push({ type: "group", group, depth });

        if (expandedGroups.has(group.id)) {
            const visibleDevices =
                searchResult.matchingDeviceIds.size > 0
                    ? group.devices.filter((d) => searchResult.matchingDeviceIds.has(d.id))
                    : group.devices;

            for (const child of group.children) {
                result.push(...flattenVisibleTree([child], expandedGroups, searchResult, depth + 1));
            }
            for (const device of visibleDevices) {
                result.push({ type: "device", device, groupId: group.id, depth: depth + 1 });
            }
        }
    }

    return result;
};

export const buildUnifiedTree = (
    groups: Group[],
    licenses: License[],
    compatibleDevices: Device[],
    compatibility?: string | null,
    parentId: number | null = null,
): GroupTreeNode[] => {
    const compatibleDevicesById = new Map(compatibleDevices.map((d) => [d.id, d]));

    const countTotalDevices = (node: GroupTreeNode): number => {
        const childrenCount = node.children.reduce((sum, child) => sum + child.totalDeviceCount, 0);
        return node.devices.length + childrenCount;
    };

    return groups
        .filter((group) => group.parentId === parentId)
        .map((group) => {
            const children = buildUnifiedTree(groups, licenses, compatibleDevices, compatibility, group.id);

            const devices: DeviceTreeNode[] = licenses
                .filter((l) => l.groupId === group.id && l.device && compatibleDevicesById.has(l.device.id))
                .map((l) => ({
                    type: "device" as const,
                    id: l.device!.id,
                    name: l.device!.name || `Device ${l.device!.id}`,
                    serial: l.device!.serial,
                    groupId: group.id,
                    incompatibility: getIncompatibility(compatibleDevicesById.get(l.device!.id)!, compatibility),
                }));

            const node: GroupTreeNode = {
                type: "group",
                id: group.id,
                name: group.name,
                parentId: group.parentId,
                children,
                devices,
                totalDeviceCount: 0,
            };

            node.totalDeviceCount = countTotalDevices(node);

            return node;
        });
};
